import React, { useEffect, useState } from 'react';
import { api, API } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { Plus, Copy, QrCode, Check, X, Infinity as InfinityIcon, Trash2 } from 'lucide-react';

export default function AdminInvite() {
  const [invites, setInvites] = useState([]);
  const [copied, setCopied] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [maxUses, setMaxUses] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get('/invites').then(r => setInvites(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const params = {};
      const n = parseInt(maxUses, 10);
      if (!Number.isNaN(n) && n > 0) params.max_uses = n;
      await api.post('/invites', null, { params });
      setShowNew(false); setMaxUses('');
      load();
    } finally { setBusy(false); }
  };

  const copyLink = async (code) => {
    const link = `${window.location.origin}/accept-invite/${code}`;
    const ok = await copyToClipboard(link, { silent: true });
    if (ok) {
      setCopied(code);
      setTimeout(() => setCopied(''), 1500);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/invites/${confirmDelete.invite_id}`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">ONBOARDING</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Invite patients</h1>
          <p className="text-fg-muted mt-2 max-w-xl">
            Share a code or printable QR poster. <strong>Each code is multi-use</strong> —
            many people can scan it, but each email may only redeem it once.
            Set a cap if you want to limit total redemptions.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary inline-flex items-center gap-2" data-testid="generate-invite-btn">
          <Plus className="w-4 h-4" /> Generate invite
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {invites.map(i => {
          const usedList = i.used_by_list || [];
          const usedCount = usedList.length || (i.used ? 1 : 0);
          const cap = i.max_uses;
          const remaining = typeof cap === 'number' ? Math.max(0, cap - usedCount) : null;
          return (
            <div key={i.invite_id} className="bento-card text-center" data-testid={`invite-card-${i.invite_id}`}>
              <div className="text-xs uppercase tracking-[0.2em] text-fg-muted mb-3">Invite code</div>
              <div className="font-mono tracking-[0.3em] text-3xl font-bold text-brand">{i.code}</div>
              <div className="mt-4 inline-block surface border border-soft p-2 rounded-lg">
                <img
                  src={`${API}/invites/${i.code}/qr`}
                  alt={`QR ${i.code}`}
                  className="w-44 h-44"
                  data-testid={`qr-${i.code}`}
                />
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <button onClick={() => copyLink(i.code)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`copy-${i.code}`}>
                  {copied === i.code ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
                  {copied === i.code ? 'Copied' : 'Copy link'}
                </button>
                <span className="badge-neutral inline-flex items-center gap-1" data-testid={`uses-${i.code}`}>
                  {usedCount} {cap == null
                    ? <>/ <InfinityIcon className="w-3 h-3 inline" /></>
                    : `/ ${cap}`} used
                </span>
                {remaining === 0
                  ? <span className="badge-neutral">Full</span>
                  : <span className="badge-success">Available</span>}
                <button onClick={() => setConfirmDelete(i)}
                        className="text-[#B83A3A] hover:bg-[#B83A3A]/10 rounded p-1.5 inline-flex items-center"
                        title="Delete this invite code"
                        data-testid={`delete-invite-${i.code}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
              {usedList.length > 0 && (
                <div className="mt-3 text-[10px] text-fg-muted text-left">
                  <div className="uppercase tracking-wider mb-1">Redeemed by</div>
                  <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                    {usedList.slice(0, 5).map((u, idx) => (
                      <li key={idx} className="truncate">· {u.email}</li>
                    ))}
                    {usedList.length > 5 && <li>… and {usedList.length - 5} more</li>}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
        {!invites.length && (
          <div className="col-span-full bento-card flex items-center justify-center text-fg-muted text-sm h-40">
            <QrCode className="w-5 h-5 mr-2" /> No invites yet — generate one.
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setConfirmDelete(null)} data-testid="delete-invite-modal">
          <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5">
              <h2 className="font-heading font-semibold mb-2">Delete invite code {confirmDelete.code}?</h2>
              <p className="text-sm text-fg-muted">
                The code and its QR poster will stop working immediately — anyone scanning it will see an
                invalid-code message. Patients who already redeemed it are not affected.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmDelete(null)} className="btn-secondary text-sm" data-testid="delete-invite-cancel">Cancel</button>
                <button onClick={doDelete} disabled={deleting}
                        className="text-sm rounded-md px-4 py-2 bg-[#B83A3A] hover:bg-[#8F2C2C] text-white disabled:opacity-50"
                        data-testid="delete-invite-confirm">
                  {deleting ? 'Deleting…' : 'Delete code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowNew(false)}>
          <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-soft flex items-center justify-between">
              <h2 className="font-heading font-semibold">New invite code</h2>
              <button onClick={() => setShowNew(false)} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <div className="text-xs text-fg-muted mb-1">Maximum redemptions (leave blank for unlimited)</div>
                <input
                  className="input"
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={maxUses}
                  onChange={e => setMaxUses(e.target.value)}
                  data-testid="invite-max-uses"
                />
              </label>
              <p className="text-xs text-fg-muted">
                Each unique email can only redeem the code once. The cap above limits the
                total number of distinct people who may use it. Leave blank for unlimited.
              </p>
            </div>
            <div className="px-5 pb-5 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={create} disabled={busy} className="btn-primary text-sm" data-testid="invite-create-confirm">
                {busy ? 'Creating…' : 'Create code'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
